import type { FeatureFlags } from '@better-lights/core';
import type { Entitlement, Me, Schemas } from '../api/types.ts';

/**
 * Plan gating (docs/spec/08-plan-features.md). Flags come from `entitlement.featureFlags` of the
 * workspace (`GET /v1/me`, cached; offline the cache is trusted until `graceUntil`, then Free).
 * Guests are Free. The table below is only a fallback for servers that don't send flags yet.
 */
export type FeatureFlag =
  | 'basic_quote'
  | 'branded_quote'
  | 'quote_templates'
  | 'hd_export'
  | 'hdr_export'
  | 'sync'
  | 'company_branding'
  | 'shared_catalog'
  | 'ai_render';

export type PlanCatalog = Schemas['PlanCatalog'];
export type PlanInfo = Schemas['PlanInfo'];

/** Plans in upgrade order (cheapest first). */
export const PLAN_ORDER = ['free', 'starter', 'pro', 'max', 'team'] as const;

const on = (...f: FeatureFlag[]): FeatureFlags => Object.fromEntries(f.map((x) => [x, true]));

/** docs/spec/08 table (fallback only). */
export const SPEC_FLAGS: Record<string, FeatureFlags> = {
  free: { ...on('basic_quote'), watermark: true },
  starter: on('basic_quote', 'branded_quote', 'hd_export', 'sync', 'ai_render'),
  pro: on('basic_quote', 'branded_quote', 'quote_templates', 'hd_export', 'hdr_export', 'sync', 'ai_render'),
  max: on('basic_quote', 'branded_quote', 'quote_templates', 'hd_export', 'hdr_export', 'sync', 'ai_render'),
  team: on(
    'basic_quote',
    'branded_quote',
    'quote_templates',
    'hd_export',
    'hdr_export',
    'sync',
    'company_branding',
    'shared_catalog',
    'ai_render',
  ),
};

export const PLAN_NAMES: Record<string, string> = {
  free: 'Free',
  starter: 'Starter',
  pro: 'Pro',
  max: 'Max',
  team: 'Team',
};

/** Flags of one entitlement (server flags win; the spec table fills gaps). */
export function flagsOf(ent: Pick<Entitlement, 'plan' | 'featureFlags'> | null | undefined): FeatureFlags {
  const plan = ent?.plan ?? 'free';
  return { ...(SPEC_FLAGS[plan] ?? SPEC_FLAGS.free), ...(ent?.featureFlags ?? {}) };
}

/** Flags for a workspace: Free for guests, for an unknown scope, or once the cached /v1/me is past grace. */
export function flagsFor(me: Me | null, scope: string | null, now = Date.now()): FeatureFlags {
  if (!me || Date.parse(me.graceUntil) <= now) return { ...SPEC_FLAGS.free };
  const ent = me.entitlements.find((e) => e.scope === (scope ?? me.personalScope));
  return flagsOf(ent ?? null);
}

export function has(flags: FeatureFlags, flag: FeatureFlag): boolean {
  return flags[flag] === true;
}

/** Monthly list price of a plan for comparison (per-seat plans × minimum seats). */
function monthlyCost(p: PlanInfo): number {
  const month = p.prices.find((x) => x.interval === 'month') ?? p.prices[0];
  if (!month) return 0;
  return month.amount * (p.seats.perSeat ? Math.max(1, p.seats.min) : 1);
}

/** The cheapest plan that includes `flag` (paywall preselection). */
export function cheapestPlanWith(flag: FeatureFlag, catalog: PlanCatalog | null): string {
  if (catalog) {
    // AI renders aren't a flag in /v1/plans: a plan "has" them when it includes monthly credits.
    const hasIt = (p: PlanInfo) => p.featureFlags[flag] === true || (flag === 'ai_render' && p.aiCredits > 0);
    const withIt = catalog.plans.filter((p) => hasIt(p) && monthlyCost(p) > 0);
    if (withIt.length) return withIt.sort((a, b) => monthlyCost(a) - monthlyCost(b))[0].id;
  }
  return PLAN_ORDER.find((p) => p !== 'free' && SPEC_FLAGS[p][flag]) ?? 'pro';
}

/** Display name of a plan id (catalog name when known). */
export function planName(id: string, catalog: PlanCatalog | null): string {
  return catalog?.plans.find((p) => p.id === id)?.name ?? PLAN_NAMES[id] ?? id;
}

/** "$9.99" from minor units (currency is ISO 4217, lowercase in the catalog). */
export function formatAmount(minor: number, currency = 'usd', locale = 'en-US'): string {
  const value = minor / 100;
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
  }).format(value);
}

/** Savings of annual vs 12 × monthly, as a whole percent (0 when not cheaper). */
export function annualSavingsPercent(p: PlanInfo): number {
  const m = p.prices.find((x) => x.interval === 'month')?.amount;
  const y = p.prices.find((x) => x.interval === 'year')?.amount;
  if (!m || !y) return 0;
  return Math.max(0, Math.round((1 - y / (12 * m)) * 100));
}

/**
 * Whether checkout may advertise a trial: `/v1/me.trialEligible` (false once the user had any trial
 * or paid plan). Signed-out visitors and servers that don't send it yet count as eligible.
 */
export function trialEligible(me: Me | null): boolean {
  return (me as (Me & { trialEligible?: boolean }) | null)?.trialEligible !== false;
}
